import { normalizeProductKey, cleanProductLabel } from '../utils/productUtils.js'

/** @import { Offer, Rivalry, ComparisonGroup, SupermarketId } from '../../types.js' */

// --- Internal helpers ---

/**
 * Parse a unit string like "200 G", "250 GRAM", "1 KG", "per stuk" into
 * a per-kg price (or per-unit if unparseable) for fair comparison.
 */
function parsePerUnit(price, unit) {
  if (!unit) return price

  const lower = unit.toLowerCase().trim()

  // Try to extract grams: "200 g", "200g", "250 gram"
  const gramMatch = lower.match(/(\d+)\s*(?:g|gram)\b/)
  if (gramMatch) {
    const grams = parseInt(gramMatch[1], 10)
    if (grams > 0) return (price / grams) * 1000 // €/kg
  }

  // Try to extract kg: "1 kg", "0.5 kg"
  const kgMatch = lower.match(/([\d.,]+)\s*kg\b/)
  if (kgMatch) {
    const kg = parseFloat(kgMatch[1].replace(',', '.'))
    if (kg > 0) return price / kg // €/kg
  }

  // Try to extract ml/liter for liquids
  const mlMatch = lower.match(/(\d+)\s*ml\b/)
  if (mlMatch) {
    const ml = parseInt(mlMatch[1], 10)
    if (ml > 0) return (price / ml) * 1000
  }

  const lMatch = lower.match(/([\d.,]+)\s*(?:l|liter)\b/)
  if (lMatch) {
    const l = parseFloat(lMatch[1].replace(',', '.'))
    if (l > 0) return price / l
  }

  // Per stuk or unparseable — use raw price
  return price
}

const EMPTY_RIVALRIES = new Map()

// --- Exported selectors ---

/** @returns {Array<ComparisonGroup>} */
export function selectComparisonGroups(state) {
  if (!state.rawData?.offers) return []

  const groups = {}
  for (const offer of state.rawData.offers) {
    const key = normalizeProductKey(offer.productName)
    if (!groups[key]) {
      groups[key] = { key, label: cleanProductLabel(offer.productName), offers: [] }
    }
    groups[key].offers.push(offer)
  }

  return Object.values(groups)
    .filter(g => {
      const uniqueSupermarkets = new Set(g.offers.map(o => o.supermarket))
      return uniqueSupermarkets.size >= 2
    })
    .sort((a, b) => b.offers.length - a.offers.length)
}

/**
 * Build a rivalry map: offerId → { rivalSupermarket, rivalPrice, rivalUnit, saving, rivalPerUnit, offerPerUnit }
 *
 * Groups all offers by normalized product key, then for each offer finds the
 * cheapest rival at a *different* supermarket. Uses per-unit price (€/kg or €/unit)
 * when units can be parsed, so comparing 200g vs 250g is fair.
 *
 * Memoised on rawData reference — only recomputes when the offers array changes.
 */
let _rivalriesCache = { ref: null, result: EMPTY_RIVALRIES }

/** @returns {Map<string, Rivalry>} */
export function selectRivalries(state) {
  if (!state.rawData?.offers) return EMPTY_RIVALRIES

  // Return cached result if rawData hasn't changed
  if (state.rawData === _rivalriesCache.ref) return _rivalriesCache.result

  const offers = state.rawData.offers
  const groups = {}

  for (const offer of offers) {
    const key = normalizeProductKey(offer.productName)
    if (!groups[key]) groups[key] = []
    groups[key].push(offer)
  }

  const result = new Map()

  for (const members of Object.values(groups)) {
    if (members.length < 2) continue

    const supermarkets = new Set(members.map(o => o.supermarket))
    if (supermarkets.size < 2) continue

    // Compute per-unit price for each offer (normalised to €/kg or €/unit)
    // Skip offers without a valid price — they can't be meaningfully compared
    const withPerUnit = members
      .filter(o => o.offerPrice != null && o.offerPrice > 0)
      .map(o => ({
        offer: o,
        perUnit: parsePerUnit(o.offerPrice, o.unit),
      }))

    for (const { offer, perUnit } of withPerUnit) {
      // Find cheapest rival at a different supermarket
      let bestRival = null
      let bestRivalPerUnit = Infinity

      for (const candidate of withPerUnit) {
        if (candidate.offer.supermarket === offer.supermarket) continue
        if (!candidate.perUnit || candidate.perUnit <= 0) continue // guard NaN/null/zero
        if (candidate.perUnit < bestRivalPerUnit) {
          bestRivalPerUnit = candidate.perUnit
          bestRival = candidate
        }
      }

      if (!bestRival) continue

      // Only show rivalry when the rival is actually cheaper
      if (bestRivalPerUnit >= perUnit) continue

      const saving = offer.offerPrice - bestRival.offer.offerPrice
      const savingPercent = Math.round((1 - bestRivalPerUnit / perUnit) * 100)

      result.set(offer.id, {
        rivalSupermarket: bestRival.offer.supermarket,
        rivalPrice: bestRival.offer.offerPrice,
        rivalUnit: bestRival.offer.unit,
        rivalSourceUrl: bestRival.offer.sourceUrl ?? null,
        saving,
        savingPercent,
        rivalPerUnit: bestRivalPerUnit,
        offerPerUnit: perUnit,
        unitsDiffer: offer.unit !== bestRival.offer.unit,
      })
    }
  }

  _rivalriesCache = { ref: state.rawData, result }
  return result
}
